import { Clear } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import React, { useCallback, useRef } from "react";

import { ValidationTextField } from "@components/ValidationContext/ValidationContext";

import { validateEnvironmentVarName } from "../utils";
import { FieldRow } from "./NewJobFormLayout";

export type EnvironmentVar = {
  name: string;
  value: string;
};

type Props = {
  rowKey: number;
  name: string;
  value: string;
  allNames: string[];
  onUpdate: (patch: Partial<EnvironmentVar>) => void;
  onClear: () => void;
};

function EnvironmentVarsFieldRow({
  rowKey,
  name,
  value,
  allNames,
  onUpdate,
  onClear,
}: Props) {
  // The value field is multiline, so it renders a <textarea>. Allow either
  // element type since the name field's '=' handler focuses this ref.
  const valueInputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const individualNameError = validateEnvironmentVarName(name);
  const nameError =
    individualNameError ||
    (allNames.filter((n) => n === name).length > 1
      ? {
          message: `${name} can only be declared once`,
          groupId: `envvar:name:${name}`,
        }
      : null);

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // '=' moves focus to the value field; everything after it goes to value
      const raw = e.target.value;
      const equalsIndex = raw.indexOf("=");
      const newName = (
        equalsIndex === -1 ? raw : raw.slice(0, equalsIndex)
      ).toUpperCase();
      onUpdate({ name: newName });
      if (equalsIndex !== -1) {
        valueInputRef.current?.focus();
      }
    },
    [onUpdate],
  );

  return (
    <FieldRow>
      <ValidationTextField
        instanceId={`envvar:name:${rowKey}`}
        required
        label="Name"
        variant="outlined"
        value={name}
        valueForValidation={name}
        onChange={handleNameChange}
        error={nameError}
      />
      <ValidationTextField
        instanceId={`envvar:value:${rowKey}`}
        label="Value"
        variant="outlined"
        multiline
        minRows={1}
        maxRows={8}
        value={value}
        valueForValidation={value}
        placeholder="empty"
        InputLabelProps={{ shrink: true }}
        inputProps={{ ref: valueInputRef }}
        onChange={(
          e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        ) => onUpdate({ value: e.target.value })}
        error={null}
        validateOthersOnBlur={[`envvar:name:${rowKey}`]}
      />
      <IconButton className="clear" onClick={onClear}>
        <Clear />
      </IconButton>
    </FieldRow>
  );
}

export default EnvironmentVarsFieldRow;
